import { styled } from '@mui/material/styles';
import { Box, Toolbar, Button, BoxProps, IconButton, Drawer } from '@mui/material';
import { NavLink } from 'react-router-dom';

// Main layout styles
export const LayoutContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  minHeight: '100vh',
}));

interface MainContentProps {
  backgroundColor?: string;
}

export const MainContent = styled(Box)<MainContentProps>(({ theme, backgroundColor }) => ({
  flexGrow: 1,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  backgroundColor: backgroundColor || '#f6f6f8',
  [theme.breakpoints.up('sm')]: {
    paddingInlineStart: '240px', // drawerWidth
  },
}));

export const ContentArea = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  flexDirection: 'column',
  padding: theme.spacing(3),
  backgroundColor: '#F6F6F8',
}));

// Toolbar styles
export const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  backgroundColor: 'white',
  borderBottom: '1px solid #F2F2F2',
}));

export const ToolbarContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  gap: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    justifyContent: 'space-between',
  },
  [theme.breakpoints.up('sm')]: {
    justifyContent: 'flex-end',
  },
}));

// Trial banner styles
interface TrialBannerProps {
  isThreeDaysOrLess: boolean;
}

export const TrialBanner = styled(Box)<TrialBannerProps>(({ theme, isThreeDaysOrLess }) => ({
  backgroundColor: isThreeDaysOrLess ? 'rgba(254, 218, 21, 1)' : 'rgba(19, 0, 128, 1)',
  width: '100%',
  height: '55px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  [theme.breakpoints.up('sm')]: {
    paddingLeft: theme.spacing(5),
    paddingRight: theme.spacing(5),
  },
}));

export const TrialContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

interface TrialTextProps {
  isThreeDaysOrLess: boolean;
  isPhone: boolean;
}

export const TrialText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isThreeDaysOrLess' && prop !== 'isPhone',
})<TrialTextProps>(({ theme, isThreeDaysOrLess, isPhone }) => ({
  color: isThreeDaysOrLess ? 'rgba(15, 23, 42, 1)' : 'white',
  fontWeight: 700,
  fontSize: isPhone ? '10px' : '14px',
  marginLeft: isPhone ? 0 : theme.spacing(0.5),
  marginRight: isPhone ? 0 : theme.spacing(0.5),
}));

interface TrialDaysTextProps {
  isThreeDaysOrLess: boolean;
}

export const TrialDaysText = styled('span')<TrialDaysTextProps>(({ isThreeDaysOrLess }) => ({
  color: isThreeDaysOrLess ? 'rgba(15, 23, 42, 1)' : 'rgba(48, 135, 233, 1)',
}));

export const TrialActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
}));

interface ActivateButtonProps {
  language: string;
}

export const ActivateButton = styled(Box)<ActivateButtonProps>(({ theme, language }) => ({
  backgroundColor: 'rgba(91, 63, 255, 1)',
  width: '141px',
  height: '31px',
  borderRadius: '20px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  fontSize: language === 'en' ? '12px' : '14px',
  color: 'rgba(234, 237, 241, 1)',
  fontWeight: 400,
  cursor: 'pointer',
}));

export const CloseButton = styled(Box)(({ theme }) => ({
  cursor: 'pointer',
  marginLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  marginTop: theme.spacing(1),
}));

// Drawer styles
export const DrawerContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  minHeight: '100%',
  paddingBottom: '16px',
  overflowY: 'auto',
}));

// Menu section wrapper for proper spacing
export const MenuSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

export const LogoContainer = styled('img')(({ theme }) => ({
  margin: theme.spacing(2),
  objectFit: 'contain',
  width: '150px',
}));

interface MenuBottomSectionProps {
  isPhone: boolean;
}

export const MenuBottomSection = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isPhone',
})<MenuBottomSectionProps>(({ theme, isPhone }) => ({
  marginTop: isPhone ? 0 : 'auto',
}));

// Menu item styles
export const MenuItemButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-start',
  textAlign: 'start',
  padding: theme.spacing(1),
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.75),
  marginBottom: theme.spacing(0.75),
  borderRadius: '12px',
  textTransform: 'none',
  boxShadow: 'none',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: 'none',
  },
  '& .MuiButton-endIcon': {
    marginInlineStart: 'auto',
    paddingLeft: '8px'
    
  },
  '& .MuiButton-startIcon': {
    paddingLeft: 0,
    marginLeft:0
  },
}));

// Child menu item styles
export const ChildMenuItemButton = styled(MenuItemButton)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  fontSize: '0.875rem',
  color: theme.palette.text.secondary,
}));

export const LogoutButton = styled(Button)(({ theme }) => ({
  justifyContent: 'flex-start',
  padding: theme.spacing(1.25),
  gap: theme.spacing(2),
  marginTop: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));

export const CollapseContainer = styled(Box)(({ theme }) => ({
  marginInlineStart: theme.spacing(2),
  paddingTop: theme.spacing(0.5),
  paddingBottom: theme.spacing(0.5),
  overflow: 'hidden',
}));

// Drawer styled components
export const TemporaryDrawer = styled(Drawer)(({ theme }) => ({
  display: 'block',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: 240, // drawerWidth
  },
}));

export const PermanentDrawer = styled(Drawer)(({ theme }) => ({
  borderColor: 'transparent !important',
  display: 'none',
  [theme.breakpoints.up('sm')]: {
    display: 'block',
  },
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: 240, // drawerWidth
  },
}));

// NavLink styles
export const StyledNavLink = styled(NavLink, {
  shouldForwardProp: (prop) => prop !== 'isSettings',
})<{ isSettings?: boolean }>(({ theme, isSettings }) => ({
  textDecoration: 'none',
  color: 'inherit',
  marginTop: isSettings ? 'auto' : '0',
}));

// Mobile menu button styles
export const MobileMenuButton = styled(IconButton)(({ theme }) => ({
  display: 'flex',
  marginRight: '16px',
  marginLeft: '16px',
  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));

// App Market Icon wrapper
export const AppMarketIconWrapper = styled(Box)(({ theme }) => ({
  '& svg': {
    fill: '#5B3FFF',
  },
}));
